import React, { useEffect, useState } from 'react';
import { useTheme } from '../context/ThemeContext';

/**
 * Client-branded splash that pops in for ~1.6s right after a successful login.
 * Trigger by writing `sessionStorage.setItem('elara_show_splash','1')` before
 * navigating to the dashboard route.
 *
 * Honors the active theme: white EYP logo on dark, black logo on light.
 */
const ClientSplash = () => {
    const { theme } = useTheme();
    const [show, setShow] = useState(false);
    const [closing, setClosing] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const trigger = () => {
            setShow(true);
            setClosing(false);
            // sequence: hold ~1.3s then fade for ~0.45s
            setTimeout(() => setClosing(true), 1300);
            setTimeout(() => setShow(false), 1750);
        };

        // 1. Initial mount: honor the flag if the page was just navigated to
        if (sessionStorage.getItem('elara_show_splash') === '1') {
            sessionStorage.removeItem('elara_show_splash');
            trigger();
        }

        // 2. Reactive trigger from anywhere in the app (e.g. LoginPage)
        const onEvent = () => {
            sessionStorage.removeItem('elara_show_splash');
            trigger();
        };
        window.addEventListener('elara:show-splash', onEvent);
        return () => window.removeEventListener('elara:show-splash', onEvent);
    }, []);

    if (!show) return null;

    const isDark = theme === 'dark';
    const logoSrc = isDark ? '/branding/eyp-white.png' : '/branding/eyp-black.png';
    const bg = isDark
        ? 'radial-gradient(ellipse at center, #0c0d11 0%, #050609 70%)'
        : 'radial-gradient(ellipse at center, #ffffff 0%, #f2f4f8 70%)';
    const accent = isDark ? 'rgba(155,135,255,0.35)' : 'rgba(120,90,255,0.18)';
    const subtleText = isDark ? 'rgba(220,220,235,0.55)' : 'rgba(40,40,60,0.55)';

    return (
        <div
            data-testid="client-splash"
            aria-hidden="true"
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                background: bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: closing ? 0 : 1,
                transition: 'opacity 420ms cubic-bezier(.4,0,.2,1)',
                pointerEvents: closing ? 'none' : 'auto',
            }}
        >
            <style>{`
                @keyframes elara-splash-glow {
                    0%   { transform: scale(0.94); opacity: 0; filter: blur(8px); }
                    45%  { transform: scale(1.02); opacity: 1; filter: blur(0); }
                    100% { transform: scale(1);    opacity: 1; filter: blur(0); }
                }
                @keyframes elara-splash-pulse {
                    0%, 100% { box-shadow: 0 0 60px 18px var(--esp-accent), 0 0 0 1px rgba(255,255,255,0.04); }
                    50%      { box-shadow: 0 0 90px 28px var(--esp-accent), 0 0 0 1px rgba(255,255,255,0.04); }
                }
                @keyframes elara-splash-bar {
                    0%   { transform: translateX(-100%); }
                    100% { transform: translateX(100%); }
                }
                @keyframes elara-splash-rise {
                    0%   { transform: translateY(10px); opacity: 0; }
                    100% { transform: translateY(0);     opacity: 1; }
                }
            `}</style>

            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 24,
                    // CSS var for the pulse keyframe
                    '--esp-accent': accent,
                }}
            >
                <div
                    style={{
                        width: 168,
                        height: 168,
                        borderRadius: 28,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                        animation:
                            'elara-splash-glow 700ms cubic-bezier(.2,.8,.25,1) both, ' +
                            'elara-splash-pulse 1800ms ease-in-out 600ms infinite',
                    }}
                >
                    <img
                        src={logoSrc}
                        alt="EYP Creations"
                        style={{
                            width: '78%',
                            height: '78%',
                            objectFit: 'contain',
                            userSelect: 'none',
                            pointerEvents: 'none',
                        }}
                    />
                </div>

                {/* Indeterminate loading bar */}
                <div
                    style={{
                        width: 168,
                        height: 2,
                        borderRadius: 2,
                        overflow: 'hidden',
                        background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                        animation: 'elara-splash-rise 500ms 400ms both ease-out',
                    }}
                >
                    <div
                        style={{
                            width: '40%',
                            height: '100%',
                            background: isDark
                                ? 'linear-gradient(90deg, transparent, #9b87ff, transparent)'
                                : 'linear-gradient(90deg, transparent, #6244ff, transparent)',
                            animation: 'elara-splash-bar 1100ms cubic-bezier(.4,0,.2,1) infinite',
                        }}
                    />
                </div>

                <div
                    style={{
                        fontSize: 11,
                        letterSpacing: '0.28em',
                        textTransform: 'uppercase',
                        color: subtleText,
                        animation: 'elara-splash-rise 500ms 600ms both ease-out',
                        userSelect: 'none',
                    }}
                >
                    Powered by <span style={{ fontWeight: 600, letterSpacing: '0.32em' }}>Elara</span>
                </div>
            </div>
        </div>
    );
};

export default ClientSplash;
